<template>
  <section class="container-full-5">
    <div class="d-flex align-items-center gap-4 mb-6">
      <button class="btn white2" @click="goBack">
        <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
        <span>Retour</span>
      </button>
      <h2 class="secondary-font gap-4 mt-2 mb-0">Membres - Détail du membre</h2>
    </div>

    <div class="clear-both h-0"></div>

    <div v-if="loading" class="flex-center py-10">
      <div class="loader dark"></div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger mb-4">
      {{ errorMessage }}
    </div>

    <!--Souscription - Component-->
    <div v-if="member" class="souscription-block widget bg-gradient-dark px-6 pt-8 pb-6 mb-5">
      <div class="souscription-title">
        <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
          <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2" alt="" />
          Souscription
        </span>
      </div>

      <div class="souscription-grid">
        <!-- Identité -->
        <div class="souscription-identity">
          <div class="souscription-avatar">
            <img :src="member.photo_url ? member.photo_url : '/public/assets/users/300w/user2.jpg'" alt="" />
          </div>
          <div class="souscription-identity-body">
            <h3 class="mb-1">{{ member.nom }} {{ member.prenom }}</h3>
            <h5 class="font-normal text-grey-200 mb-3">{{ member.collectivite?.nom || 'N/A' }}</h5>
            <p class="mb-1">{{ member.telephone || '-' }}</p>
            <p class="souscription-email mb-3">{{ member.email || '-' }}</p>

            <p class="text-sm text-grey-300 mb-1" v-if="member.date_engagement">Date d'engagement</p>
            <p class="font-semibold mb-3" v-if="member.date_engagement">
              {{ formatDateFrench(member.date_engagement) }} · {{ calculateAnciennete(member.date_engagement) }}
            </p>

            <span v-if="member.actif" class="tag-state notif stroke">Actif</span>
            <span v-else class="tag-state danger stroke">Inactif</span>
          </div>
        </div>

        <!-- Dates / financier -->
        <div class="souscription-dates">
          <div>
            <p class="text-sm text-grey-300 mb-1">Date de souscription</p>
            <p class="font-semibold">{{ member.created_at ? formatDateFrench(member.created_at) : '-' }}</p>
          </div>
          <div>
            <p class="text-sm text-grey-300 mb-1">Date de fin de souscription</p>
            <p class="font-semibold">{{ member.souscription?.date_fin ? formatDateFrench(member.souscription.date_fin) : '-' }}</p>
          </div>
          <hr class="souscription-hr" />
          <div>
            <p class="text-sm text-grey-300 mb-1">Utilisation total</p>
            <p class="font-semibold">{{ member.balance ? formatAmount(member.balance) + ' F' : '0 F' }}</p>
          </div>
          <div>
            <p class="text-sm text-grey-300 mb-1">Salaire Net après utilisation</p>
            <p class="font-semibold">{{ member.salaire ? formatAmount((member.salaire || 0) - (member.balance || 0)) + ' F' : '-' }}</p>
          </div>
          <div>
            <p class="text-sm text-grey-300 mb-1">Quotité cessible disponible</p>
            <p class="font-semibold">
              {{ member.quotite_cessible_pourcent && member.quotite_cessible_montant
                ? `${member.quotite_cessible_pourcent}% · ${formatAmount(member.quotite_cessible_montant)} F`
                : '-' }}
            </p>
          </div>
        </div>

        <!-- Widgets + barre utilisation -->
        <div class="souscription-widgets">
          <div class="souscription-widgets-grid">
            <div class="souscription-card">
              <p class="souscription-card-label">Salaire Net</p>
              <p class="souscription-card-value">{{ member.salaire ? formatAmount(member.salaire) + ' F' : '-' }}</p>
            </div>
            <div class="souscription-card">
              <p class="souscription-card-label">Prélèvement sur salaire</p>
              <p class="souscription-card-value">{{ member.quotite_cessible_montant ? formatAmount(member.quotite_cessible_montant) + ' F' : '-' }}</p>
              <p class="souscription-card-extra">{{ member.quotite_cessible_pourcent ? member.quotite_cessible_pourcent + '%' : '' }}</p>
            </div>
            <div class="souscription-card">
              <p class="souscription-card-label">Mon revolving</p>
              <p class="souscription-card-value">{{ member.balance ? formatAmount(member.balance) + ' F' : '-' }}</p>
              <p class="souscription-card-extra">Par mois</p>
            </div>
            <div class="souscription-card">
              <p class="souscription-card-label">Pouvoir d'achat</p>
              <p class="souscription-card-value">{{ member.souscription?.montant ? formatAmount(member.souscription.montant) + ' F' : '-' }}</p>
              <p class="souscription-card-extra">{{ member.souscription?.duree_mois ? member.souscription.duree_mois + ' mois' : '' }}</p>
            </div>
          </div>

          <div class="souscription-progress mt-6">
            <div class="souscription-progress-header mb-2">
              <span class="font-semibold text-grey-100">Utilisation</span>
              <span class="text-sm text-grey-200">{{ quotiteUsedPercent }}%</span>
            </div>
            <div class="souscription-progress-bar">
              <div class="souscription-progress-fill bg-pop-green" :style="`width: ${Math.max(quotiteUsedPercent, 2)}%;`"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="member" class="clear-both h-5"></div>
    <div v-if="member" class="step vertical bg-grey-500 ml-auto mr-auto"></div>
    <div v-if="member" class="clear-both h-5"></div>

    <!--Identification / Bulletin - Component-->
    <div v-if="member" class="s20 widget px-6 pt-8 pb-2 mb-5">
      <div class="s10 mt--4 ml--4 mb-5 collapse-open-flex active">
        <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
          <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
          <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
          Identification / Bulletin
        </span>
      </div>
      <div class="s10 mt--5-5 flex-right gap-3">
        <button class="btn-ultra" style="color: var(--color-white) !important;" @click="goToKyc">
          <div class="btn-show">
            <img src="/public/icons/icon-edit.svg" class="btn-icon icon-white" alt="" />
            <span style="color: var(--color-white);">Editer</span>
          </div>
        </button>
        <button class="btn-ultra danger" :disabled="kycActionLoading || !member.kyc || kycStatut === 'rejected'" @click="rejectKyc">
          <div class="btn-show">
            <img src="/public/icons/icon-denied.svg" class="btn-icon icon-white" alt="" />
            <span style="color: var(--color-white);">Rejeter</span>
          </div>
        </button>
        <button class="btn-ultra" :disabled="kycActionLoading || !member.kyc || kycStatut === 'verified'" @click="validateKyc">
          <div class="btn-show">
            <img src="/public/icons/icon-valider.svg" class="btn-icon icon-neutral" alt="" />
            <span>Valider</span>
          </div>
        </button>
      </div>
      <div class="clear-both h-5"></div>

      <div class="s20 items-center gap-6 px-5 pb-6 collapse-content-flex active">
        <div class="s7_mobile pt-5">
          <h3 class="font-normal text-primary-color mb-4">
            Identification &nbsp;
            <span v-if="kycStatut === 'verified'" class="tag-state notif stroke">Validé</span>
            <span v-else-if="kycStatut === 'rejected'" class="tag-state danger stroke">Rejeté</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <div class="s6 rounded-50 overflow-hidden item">
            <img :src="member.photo_url ? member.photo_url : '/public/assets/users/300w/user2.jpg'" width="100%" />
          </div>
          <div class="s14 mt-5 px-5">
            <h4 class="text-grey-900 mb-3" v-if="member.kyc?.id">n° CF{{ member.created_at ? moment(member.created_at).format('YYYY') : '2025' }}-{{ String(member.kyc.id).padStart(6, '0') }}</h4>
            <h4 class="text-grey-900 mb-3" v-else>n° -</h4>
            <h5 class="font-normal my-1 text-grey-800" v-if="member.kyc?.created_at">Effectuée le</h5>
            <h5 class="font-normal my-1 text-grey-800" v-if="member.kyc?.created_at">{{ formatDate(member.kyc.created_at, 'DD/MM/YYYY HH:mm') }}</h5>
            <h5 class="text-grey-900 mt-4">{{ member.nom }} {{ member.prenom }}</h5>
          </div>
        </div>
        <div class="flex-center"><hr class="hr-vertical h-100p" /></div>

        <div class="s6_mobile px-6">
          <h3 class="font-normal text-primary-color mb-4">
            Pièce d'identité &nbsp;
            <span v-if="kycStatut === 'verified'" class="tag-state notif stroke">Validé</span>
            <span v-else-if="kycStatut === 'rejected'" class="tag-state danger stroke">Rejeté</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <h4 class="text-grey-900 my-2" v-if="member.numero_piece_identite">N° {{ member.numero_piece_identite }} / {{ member.type_piece_identite || 'CNI' }}</h4>
          <h4 class="text-grey-900 my-2" v-else-if="member.kyc?.piece_numero">N° {{ member.kyc.piece_numero }} / {{ member.kyc?.piece_type || 'CNI' }}</h4>
          <h4 class="text-grey-900 my-2" v-else>N° - / -</h4>
          <h6 class="font-normal mb-4 text-grey-800" v-if="member.kyc?.piece_expiration">
            Date d'expiration <b>{{ formatDateFrench(member.kyc.piece_expiration) }}</b>
          </h6>
          <h6 class="font-normal mb-4 text-grey-800" v-else>Date d'expiration <b>-</b></h6>
          <div class="flex gap-4" v-if="member.document_identite_recto || member.document_identite_verso">
            <img v-if="member.document_identite_recto" :src="member.document_identite_recto.fichier_url" width="100%" />
            <img v-if="member.document_identite_verso" :src="member.document_identite_verso.fichier_url" width="100%" />
          </div>
          <div class="flex gap-4" v-else-if="member.kyc && member.kyc.kyc_documents && member.kyc.kyc_documents.length > 0">
            <img v-for="(doc, idx) in member.kyc.kyc_documents.slice(0, 2)" :key="idx" :src="doc.fichier_url" width="100%" />
          </div>
          <div class="flex gap-4" v-else>
            <div class="text-grey-500">Aucun document disponible</div>
          </div>
        </div>
        <div class="flex-center"><hr class="hr-vertical h-100p" /></div>

        <div class="s7_mobile px-6">
          <h3 class="font-normal text-primary-color mb-4">
            Bulletins de salaires &nbsp;
            <span v-if="kycStatut === 'verified'" class="tag-state notif stroke">Validé</span>
            <span v-else-if="kycStatut === 'rejected'" class="tag-state danger stroke">Rejeté</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <template v-if="member.bulletin1_document || member.bulletin2_document || member.bulletin3_document">
            <a v-if="member.bulletin1_document" :href="member.bulletin1_document.fichier_url" target="_blank">
              <h5 class="font-normal mb-2 text-grey-800">Bulletin 1 - <span class="text-grey-500">{{ formatDateFrench(member.bulletin1_document.created_at) }}</span></h5>
              <h5 class="mb-5 text-grey-800"><img src="/public/icons/icon-download.svg" class="size-sm mr-2" alt="" />{{ member.bulletin1_document.fichier || 'fichier_bulletin.pdf' }}</h5>
            </a>
            <a v-if="member.bulletin2_document" :href="member.bulletin2_document.fichier_url" target="_blank">
              <h5 class="font-normal mb-2 text-grey-800">Bulletin 2 - <span class="text-grey-500">{{ formatDateFrench(member.bulletin2_document.created_at) }}</span></h5>
              <h5 class="mb-5 text-grey-800"><img src="/public/icons/icon-download.svg" class="size-sm mr-2" alt="" />{{ member.bulletin2_document.fichier || 'fichier_bulletin.pdf' }}</h5>
            </a>
            <a v-if="member.bulletin3_document" :href="member.bulletin3_document.fichier_url" target="_blank">
              <h5 class="font-normal mb-2 text-grey-800">Bulletin 3 - <span class="text-grey-500">{{ formatDateFrench(member.bulletin3_document.created_at) }}</span></h5>
              <h5 class="mb-5 text-grey-800"><img src="/public/icons/icon-download.svg" class="size-sm mr-2" alt="" />{{ member.bulletin3_document.fichier || 'fichier_bulletin.pdf' }}</h5>
            </a>
          </template>
          <template v-else>
            <p class="text-grey-500">Aucun bulletin disponible</p>
          </template>
        </div>
      </div>
    </div>

    <div v-if="member" class="clear-both h-5"></div>
    <div v-if="member" class="step vertical bg-grey-500 ml-auto mr-auto"></div>
    <div v-if="member" class="clear-both h-5"></div>

    <!--Contrat d'engagement - Component-->
    <div v-if="member" class="s20 widget px-6 pt-8 pb-2 mb-5">
      <div class="s10 mt--4 ml--4 mb-5 collapse-open-flex active">
        <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
          <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
          <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
          Contrat d'engagement
        </span>
      </div>
      <div class="clear-both h-5"></div>

      <div class="s20 items-center gap-6 px-5 pb-6 collapse-content-flex active">
        <div class="s7_mobile pt-5">
          <h3 class="font-normal text-primary-color mb-4">
            Coop Finance &nbsp;
            <span v-if="member.souscription_id" class="tag-state notif stroke">Validé</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <h4 class="text-grey-900" v-if="member.souscription_id">Contrat n° / CF{{ member.created_at ? moment(member.created_at).format('YYYY') : '2025' }}-{{ String(member.souscription_id).padStart(6, '0') }}</h4>
          <h4 class="text-grey-900" v-else>Contrat n° / -</h4>
          <h5 class="font-normal my-2 text-grey-800" v-if="member.souscription?.created_at">Signé le {{ formatDateFrench(member.souscription.created_at) }}</h5>
          <h5 class="font-normal my-2 text-grey-800" v-else>Signé le -</h5>
          <h6 class="font-semibold my-2 text-grey-800" v-if="member.date_engagement">Durée: {{ calculateAnciennete(member.date_engagement) }}</h6>
          <h6 class="font-semibold my-2 text-grey-800" v-else>Durée: -</h6>
          <div class="clear-both h-10"></div>
          <h4 class="font-normal mb-2 text-grey-800">Montant du contrat</h4>
          <h4 class="mb-2 text-grey-800" v-if="member.souscription?.montant">{{ formatAmount(member.souscription.montant) }} F</h4>
          <h4 class="mb-2 text-grey-800" v-else>-</h4>
          <div class="clear-both h-20"></div>
          <span v-if="member.actif" class="tag-state notif stroke">Actif</span>
          <span v-else class="tag-state danger stroke">Inactif</span>
        </div>
        <div class="flex-center"><hr class="hr-vertical h-100p" /></div>

        <div class="s7_mobile px-6">
          <h3 class="font-normal text-primary-color mb-4">
            {{ member.collectivite?.nom || 'Collectivité' }} &nbsp;
            <span v-if="member.collectivite?.id" class="tag-state notif stroke">Validé</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <h4 class="text-grey-900 my-2">Identifiant / {{ member.email || member.telephone || 'N/A' }}</h4>
          <h6 class="font-normal mb-4 text-grey-800" v-if="member.date_engagement">Date de début de contrat <br class="mb-2" /><b>{{ formatDateFrench(member.date_engagement) }}</b></h6>
          <h6 class="font-normal mb-4 text-grey-800" v-else>Date de début de contrat <br class="mb-2" /><b>-</b></h6>
          <h6 class="font-normal mb-4 text-grey-800" v-if="member.souscription?.date_fin">Date de fin de contrat <br class="mb-2" /><b>{{ formatDateFrench(member.souscription.date_fin) }}</b></h6>
          <h6 class="font-normal mb-4 text-grey-800" v-else>Date de fin de contrat <br class="mb-2" /><b>-</b></h6>
        </div>
        <div class="flex-center"><hr class="hr-vertical h-100p" /></div>

        <div class="s6_mobile px-6">
          <h3 class="font-normal text-primary-color mb-4">
            Membre &nbsp;
            <span v-if="member.email_verified_at && member.actif" class="tag-state notif stroke">Validé</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <h4 class="text-grey-900 my-2">Identifiant / {{ member.email || member.telephone || 'N/A' }}</h4>
          <h4 class="text-grey-900 my-2">{{ member.nom }} {{ member.prenom }}</h4>
          <h6 class="font-normal mb-4 text-grey-800" v-if="member.date_engagement">Date de début de contrat <br class="mb-2" /><b>{{ formatDateFrench(member.date_engagement) }}</b></h6>
          <h6 class="font-normal mb-4 text-grey-800" v-else>Date de début de contrat <br class="mb-2" /><b>-</b></h6>
          <div class="clear-both h-10"></div>
          <h4 class="font-normal mb-2 text-grey-800">Prélèvement mensuel</h4>
          <h4 class="mb-2 text-grey-800" v-if="member.quotite_cessible_montant">{{ formatAmount(member.quotite_cessible_montant) }} F</h4>
          <h4 class="mb-2 text-grey-800" v-else>-</h4>
        </div>
      </div>
    </div>

    <div v-if="member" class="clear-both h-5"></div>
    <div v-if="member" class="step vertical bg-grey-500 ml-auto mr-auto"></div>
    <div v-if="member" class="clear-both h-5"></div>

    <!--Premier prélèvement / Carte - Component-->
    <div v-if="member" class="s20 widget px-6 pt-8 pb-2 mb-5">
      <div class="s10 mt--4 ml--4 mb-5 collapse-open-flex active">
        <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
          <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
          <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
          Premier prélèvement / Carte
        </span>
      </div>
      <div class="s10 mt--5-5 flex-right">
        <button class="btn lg py-3 white2 active" @click="goToCarte">
          <img src="/public/icons/icon-consulter.svg" class="btn-icon" alt="" />
          <span>Carte</span>
        </button>
      </div>
      <div class="clear-both h-0"></div>

      <div class="s20 items-center gap-6 px-5 pb-6 collapse-content-flex active">
        <div class="s7_mobile pt-6">
          <h3 class="font-normal text-primary-color mb-4">
            Premier prélèvement &nbsp;
            <span v-if="member.souscription_id" class="tag-state notif stroke">Validé</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <h4 class="text-grey-900" v-if="member.souscription_id">Signature / CF{{ member.created_at ? moment(member.created_at).format('YYYY') : '2025' }}-{{ String(member.souscription_id).padStart(6, '0') }}</h4>
          <h4 class="text-grey-900" v-else>Signature / -</h4>
          <h5 class="font-normal my-2 text-grey-800" v-if="member.souscription?.created_at">Effectué le {{ formatDate(member.souscription.created_at, 'DD/MM/YYYY HH:mm') }}</h5>
          <h5 class="font-normal my-2 text-grey-800" v-else>-</h5>
          <div class="clear-both h-10"></div>
          <h4 class="font-normal mb-2 text-grey-800">Montant du prélèvement</h4>
          <h4 class="mb-2 text-grey-800" v-if="member.quotite_cessible_montant">{{ formatAmount(member.quotite_cessible_montant) }} F</h4>
          <h4 class="mb-2 text-grey-800" v-else>-</h4>
        </div>
        <div class="flex-center"><hr class="hr-vertical h-100p" /></div>

        <div class="s7_mobile px-6">
          <h3 class="font-normal text-primary-color mb-4">
            Création de la carte &nbsp;
            <span v-if="member.cartes && member.cartes.length > 0" class="tag-state notif stroke">Validé</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <h4 class="text-grey-900 my-2" v-if="member.cartes && member.cartes.length > 0">**** **** **** {{ member.cartes[0].bloc4 || (member.cartes[0].numero_masque ? member.cartes[0].numero_masque.slice(-4) : '****') }}</h4>
          <h4 class="text-grey-900 my-2" v-else>**** **** **** ****</h4>
          <h4 class="text-grey-900 my-2">{{ member.collectivite?.nom || 'N/A' }}</h4>
          <h5 class="font-normal my-2 text-grey-800" v-if="member.cartes && member.cartes[0]?.created_at">Créé le {{ formatDate(member.cartes[0].created_at, 'DD/MM/YYYY HH:mm') }}</h5>
          <h5 class="font-normal my-2 text-grey-800" v-else>-</h5>
          <div class="clear-both h-10"></div>
          <h4 class="text-grey-800" v-if="member.cartes && member.cartes[0]?.reference">Référence / {{ member.cartes[0].reference }}</h4>
          <h4 class="text-grey-800" v-else>Référence / -</h4>
          <div class="clear-both h-10"></div>
          <h4 class="font-normal mb-2 text-grey-800">Montant disponible</h4>
          <h4 class="mb-2 text-grey-800" v-if="member.cartes && member.cartes[0]">{{ formatAmount(member.cartes[0].solde || 0) }} F</h4>
          <h4 class="mb-2 text-grey-800" v-else>-</h4>

          <button v-if="hasCarteEnAttenteRetrait" class="btn lg full danger mt-5" style="white-space: nowrap;" @click="goToCarte">
            <img src="/public/icons/icon-valider.svg" class="btn-icon icon-white" alt="" />
            <span style="color: var(--color-white);">Retrait de carte</span>
          </button>
        </div>
        <div class="flex-center"><hr class="hr-vertical h-100p" /></div>

        <div class="s6_mobile px-6">
          <h3 class="font-normal text-primary-color mb-4">
            Livraison &nbsp;
            <span v-if="member.cartes && member.cartes[0]?.created_at" class="tag-state notif stroke">Validé</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <h5 class="text-grey-900 my-2">Point relais - collectivité</h5>
          <h5 class="font-normal my-2 text-grey-800" v-if="member.cartes && member.cartes[0]?.created_at">Livré le {{ formatDate(member.cartes[0].created_at, 'DD/MM/YYYY HH:mm') }}</h5>
          <h5 class="font-normal my-2 text-grey-800" v-else>-</h5>
          <div class="clear-both h-20"></div>
          <h3 class="font-normal text-primary-color mb-4">
            Réception &nbsp;
            <span v-if="member.cartes && member.cartes[0]?.date_retrait" class="tag-state notif stroke">Validé</span>
            <span v-else class="tag-state warning stroke">En attente</span>
          </h3>
          <h5 class="text-grey-900 my-2">Point relais - collectivité</h5>
          <h5 class="font-normal my-2 text-grey-800" v-if="member.cartes && member.cartes[0]?.date_retrait">Reçu le {{ formatDate(member.cartes[0].date_retrait, 'DD/MM/YYYY HH:mm') }}</h5>
          <h5 class="font-normal my-2 text-grey-500" v-else>Carte non encore retirée</h5>
        </div>
      </div>
    </div>

    <div v-if="member" class="clear-both h-5"></div>
    <div v-if="member" class="step vertical bg-grey-500 ml-auto mr-auto"></div>
    <div v-if="member" class="clear-both h-5"></div>

    <!--Prélèvement mensuel - Component-->
    <div v-if="member" class="s20 widget px-6 pt-8 pb-2">
      <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
        <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
          <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
          <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
          Prélèvement mensuel
        </span>
      </div>

      <div class="s20 items-center gap-6 pb-4 collapse-content active">
        <div class="clear-both h-20"></div>

        <!--Barre de progression-->
        <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
          <div class="widget-floating py-0 text-xl flex-right">{{ prelevementsStats.percent }}%</div>
          <div class="h-100p flex align-items-center rounded-100 px-7 bg-pop-green" :style="`width: ${progressWidth}%;`">
            <h2>{{ formatAmount(prelevementsStats.total_effectue) }} F</h2>
            <h2 class="font-medium">&nbsp;/ {{ formatAmount(prelevementsStats.total_attendu) }} F</h2>
          </div>
        </div>

        <div class="clear-both h-30"></div>

        <div v-if="prelevementsLoading" class="text-center py-5 text-grey-500">
          Chargement des prélèvements mensuels...
        </div>

        <div v-else-if="prelevementsMensuels.length === 0" class="text-center py-5 text-grey-500">
          Aucun prélèvement mensuel disponible pour ce membre.
        </div>

        <div v-else class="px-1" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 1rem;">
          <div
            v-for="detail in prelevementsMensuels"
            :key="detail.id"
            class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5"
            :class="{ active: detail.statut === 'done', 'opacity-6': detail.statut === 'cancelled' }"
          >
            <div class="s10">
              <h6 class="secondary-font font-semibold mb-1">{{ formatPrelevementDateLong(detail.date_prevision) }}</h6>
              <h3 class="secondary-font font-semibold text-grey-900 py-1" style="white-space: nowrap;">{{ formatAmount(detail.montant) }}&nbsp;F</h3>
            </div>
            <div class="s10 text-right">
              <h5 v-if="detail.statut === 'pending' && detail.countdown" class="secondary-font font-semibold text-grey-600 pb-2">{{ detail.countdown }}</h5>
              <div v-if="detail.statut === 'done'" class="mb-4">
                <span class="tag-state notif stroke">Effectué</span>
              </div>
              <div v-else-if="detail.statut === 'cancelled'" class="mb-4">
                <span class="tag-state danger stroke">Annulé</span>
              </div>
              <h6 v-if="detail.statut === 'done' && detail.date_operation" class="secondary-font font-semibold text-grey-600 pt-1 mb-4">
                Le {{ formatPrelevementDate(detail.date_operation) }}
              </h6>
              <button
                v-if="detail.statut === 'pending'"
                class="btn-ultra w-100 shadow-0"
                :disabled="validatingPrelevementId !== null"
                @click="validatePrelevementMensuel(detail)"
              >
                <div class="btn-show" :class="{ 'display-none': validatingPrelevementId === detail.id }">
                  <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
                  <span>Valider</span>
                </div>
                <div class="btn-loading" :class="{ 'display-none': validatingPrelevementId !== detail.id }">
                  <div class="loader-dots"></div>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div class="clear-both h-15"></div>
      </div>
    </div>

    <div v-if="member" class="clear-both h-5"></div>
    <div v-if="member" class="step vertical bg-grey-500 ml-auto mr-auto"></div>
    <div v-if="member" class="clear-both h-5"></div>

    <!--Décaissement mensuel - Component-->
    <div v-if="member" class="s20 widget px-6 pt-8 pb-2">
      <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
        <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
          <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
          <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
          Décaissement mensuel
        </span>
      </div>

      <div class="s20 items-center gap-6 px-5 pb-4 collapse-content active">
        <div class="clear-both h-10"></div>
        <div class="flex items-center gap-6">
          <div class="s7">
            <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
              <div class="circle-medium bg-color-1000"></div>
              Débloqué
            </h5>
            <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(decaissementsStats.debloque) }} F</h1>
            <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ decaissementsStats.debloque_percent }}%</h5>
            <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
              <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="`width: ${decaissementsStats.debloque_percent}%;`">
                {{ decaissementsStats.debloque_percent }}%
              </div>
            </div>
          </div>
          <div class="s7">
            <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
              <div class="circle-medium bg-purple"></div>
              Bloqué
            </h5>
            <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(decaissementsStats.bloque) }} F</h1>
            <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ decaissementsStats.bloque_percent }}%</h5>
            <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
              <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-purple text-white" :style="`width: ${decaissementsStats.bloque_percent}%;`">
                {{ decaissementsStats.bloque_percent }}%
              </div>
            </div>
          </div>
          <div class="s6">
            <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
              <div class="circle-medium bg-green-light"></div>
              Total
            </h5>
            <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(decaissementsStats.total) }} F</h1>
            <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
            <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
              <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                100%
              </div>
            </div>
          </div>
        </div>

        <div class="clear-both h-30"></div>

        <div v-if="decaissementsLoading" class="text-center py-5 text-grey-500">
          Chargement des décaissements mensuels...
        </div>

        <div v-else-if="decaissementsMensuels.length === 0" class="text-center py-5 text-grey-500">
          Aucun décaissement mensuel disponible pour ce membre.
        </div>

        <div v-else class="px-1" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 1rem;">
          <div
            v-for="detail in decaissementsMensuels"
            :key="detail.id"
            class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5"
            :class="{ active: detail.statut === 'done', 'opacity-6': detail.statut === 'cancelled' }"
          >
            <div class="s10">
              <h6 class="secondary-font font-semibold mb-1">{{ formatPrelevementDateLong(detail.date_prevision) }}</h6>
              <h3 class="secondary-font font-semibold text-grey-900 py-1" style="white-space: nowrap;">{{ formatAmount(detail.montant) }}&nbsp;F</h3>
              <p class="secondary-font font-semibold text-purple">{{ detail.percent }}%</p>
            </div>
            <div class="s10 text-right">
              <h5 v-if="detail.statut === 'pending' && detail.countdown" class="secondary-font font-semibold text-primary-color pb-1">{{ detail.countdown }}</h5>
              <div v-if="detail.statut === 'done'" class="mb-4">
                <span class="tag-state notif stroke">Effectué</span>
              </div>
              <div v-else-if="detail.statut === 'cancelled'" class="mb-4">
                <span class="tag-state danger stroke">Annulé</span>
              </div>
              <p v-if="detail.statut === 'done' && detail.date_operation" class="secondary-font font-semibold text-grey-600 pt-1">Date de déblocage</p>
              <h6 v-if="detail.statut === 'done' && detail.date_operation" class="secondary-font font-semibold text-grey-600 py-1">{{ formatPrelevementDate(detail.date_operation) }}</h6>
            </div>
          </div>
        </div>

        <div class="clear-both h-15"></div>
      </div>
    </div>

    <div v-if="member" class="clear-both h-5"></div>
    <div v-if="member" class="step vertical bg-grey-500 ml-auto mr-auto"></div>
    <div v-if="member" class="clear-both h-5"></div>

    <!--Wallet / Utilisation - Component-->
    <div v-if="member" class="s20 widget px-6 pt-8 pb-2">
      <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
        <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
          <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
          <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
          Wallet / Utilisation
        </span>
      </div>

      <div class="s20 gap-6 px-5 pb-8 collapse-content active">
        <div class="clear-both h-10"></div>
        <div v-if="walletLoading" class="text-center py-5 text-grey-500">
          Chargement du wallet...
        </div>
        <div v-else class="flex items-center gap-6">
          <div class="s7">
            <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
              <div class="circle-medium bg-color-1000"></div>
              Solde
            </h5>
            <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(walletInfos.solde) }} F</h1>
            <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ walletInfos.solde_percent }}%</h5>
            <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
              <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="`width: ${walletInfos.solde_percent}%;`">
                {{ walletInfos.solde_percent }}%
              </div>
            </div>
          </div>
          <div class="s7">
            <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
              <div class="circle-medium bg-purple"></div>
              Montant Utilisé
            </h5>
            <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(walletInfos.utilise) }} F</h1>
            <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ walletInfos.utilise_percent }}%</h5>
            <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
              <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-purple text-white" :style="`width: ${walletInfos.utilise_percent}%;`">
                {{ walletInfos.utilise_percent }}%
              </div>
            </div>
          </div>
          <div class="s6">
            <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
              <div class="circle-medium bg-green-light"></div>
              Dernier décaissement
            </h5>
            <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(walletInfos.dernier_decaissement) }} F</h1>
            <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ walletInfos.dernier_decaissement_percent }}%</h5>
            <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
              <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" :style="`width: ${walletInfos.dernier_decaissement_percent}%;`">
                {{ walletInfos.dernier_decaissement_percent }}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import moment from 'moment'
import membersService from '@/services/membersService'
import { showAlert, showConfirm } from '@/utils/swal'

interface PrelevementMensuel {
  id: number
  souscription_id: number
  mois: number
  date_prevision: string | null
  date_operation: string | null
  montant: number
  statut: 'pending' | 'done' | 'cancelled'
  countdown: string | null
}

interface DecaissementMensuel {
  id: number
  mois: number
  date_prevision: string | null
  date_operation: string | null
  montant: number
  percent: number
  statut: 'pending' | 'done' | 'cancelled'
  countdown: string | null
}

interface Member {
  id: number
  nom: string
  prenom: string
  collectivite?: {
    id: number
    nom: string
  }
  photo?: string
  photo_url?: string
  email?: string
  telephone?: string
  created_at?: string
  last_connection_at?: string
  actif?: number
  deleted_at?: string | null
  date_engagement?: string | null
  date_naissance?: string | null
  fonction?: string | null
  salaire?: number | null
  balance?: number | null
  quotite_cessible_pourcent?: number | null
  quotite_cessible_montant?: number | null
  souscription_id?: number | null
  souscription?: any
  kyc?: any
  cartes?: any[]
  numero_piece_identite?: string | null
  type_piece_identite?: string | null
  bulletin1_document?: any
  bulletin2_document?: any
  bulletin3_document?: any
  document_identite_recto?: any
  document_identite_verso?: any
  email_verified_at?: string | null
}

const route = useRoute()
const router = useRouter()
const member = ref<Member | null>(null)
const loading = ref(false)
const errorMessage = ref('')

const prelevementsMensuels = ref<PrelevementMensuel[]>([])
const prelevementsStats = ref({ total_attendu: 0, total_effectue: 0, percent: 0 })
const prelevementsLoading = ref(false)
const validatingPrelevementId = ref<number | null>(null)

const decaissementsMensuels = ref<DecaissementMensuel[]>([])
const decaissementsStats = ref({
  debloque: 0,
  debloque_percent: 0,
  bloque: 0,
  bloque_percent: 0,
  total: 0,
})
const decaissementsLoading = ref(false)

const walletInfos = ref({
  solde: 0,
  solde_percent: 0,
  utilise: 0,
  utilise_percent: 0,
  dernier_decaissement: 0,
  dernier_decaissement_percent: 0,
  dernier_decaissement_date: null as string | null,
})
const walletLoading = ref(false)

const cartes = ref<Array<{ id: number; statut: string }>>([])
const hasCarteEnAttenteRetrait = computed(() => cartes.value.some(c => c.statut === 'delivery'))

const kycActionLoading = ref(false)
const kycStatut = computed(() => member.value?.kyc?.statut)

const progressWidth = computed(() => Math.min(prelevementsStats.value.percent, 100))

const quotiteUsedPercent = computed(() => {
  const m = member.value
  if (!m?.quotite_cessible_montant || !m.salaire || m.salaire <= 0) return 0
  return 0
})

function formatAmount(amount: number | null | undefined): string {
  return new Intl.NumberFormat('fr-FR').format(amount || 0)
}

function formatDate(date: string | null | undefined, format = 'DD/MM/YYYY'): string {
  if (!date) return '-'
  const m = moment(date)
  if (!m.isValid()) return '-'
  return m.format(format)
}

function formatDateFrench(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
  const date = moment(dateString)
  if (!date.isValid()) return '-'
  return `${date.date()} ${monthsFr[date.month()]} ${date.year()}`
}

function calculateAnciennete(dateEngagement: string | null | undefined): string {
  if (!dateEngagement) return '-'
  const engagementDate = moment(dateEngagement)
  if (!engagementDate.isValid()) return '-'
  const now = moment()
  const years = now.diff(engagementDate, 'years')
  const months = now.diff(engagementDate, 'months') % 12
  if (years > 0 && months > 0) return `${years} an${years > 1 ? 's' : ''} ${months} mois`
  if (years > 0) return `${years} an${years > 1 ? 's' : ''}`
  if (months > 0) return `${months} mois`
  return '0 mois'
}

async function validateKyc() {
  if (!member.value?.kyc) {
    await showAlert.warning('Aucun KYC à valider pour ce membre.', 'Information')
    return
  }
  const ok = await showConfirm('Confirmer la validation du KYC de ce membre ?', 'Validation KYC')
  if (!ok) return
  kycActionLoading.value = true
  try {
    await axios.post(`/kyc/members/${route.params.id}/validate`)
    await loadMember(member.value.id)
    await showAlert.success('KYC validé.', 'Succès')
  } catch (e: any) {
    await showAlert.error(e.response?.data?.message || 'Erreur lors de la validation.', 'Erreur')
  } finally {
    kycActionLoading.value = false
  }
}

async function rejectKyc() {
  if (!member.value?.kyc) {
    await showAlert.warning('Aucun KYC à rejeter pour ce membre.', 'Information')
    return
  }
  const ok = await showConfirm('Confirmer le rejet du KYC de ce membre ?', 'Rejet KYC')
  if (!ok) return
  kycActionLoading.value = true
  try {
    await axios.post(`/kyc/members/${route.params.id}/reject`)
    await loadMember(member.value.id)
    await showAlert.success('KYC rejeté.', 'Succès')
  } catch (e: any) {
    await showAlert.error(e.response?.data?.message || 'Erreur lors du rejet.', 'Erreur')
  } finally {
    kycActionLoading.value = false
  }
}

function goToKyc() {
  if (member.value) {
    router.push({ name: 'collectivites-kyc-membre-detail', params: { id: member.value.id } })
  }
}

onMounted(async () => {
  const memberId = route.params.id
  if (memberId) {
    await loadMember(Number(memberId))
    await loadPrelevementsMensuels(Number(memberId))
    await loadDecaissementsMensuels(Number(memberId))
    await loadWalletInfos(Number(memberId))
    await loadCartes(Number(memberId))
  } else {
    router.push({ name: 'collectivites-membres' })
  }
})

async function loadPrelevementsMensuels(id: number) {
  prelevementsLoading.value = true
  try {
    const data = await membersService.getMemberPrelevementsMensuels(id)
    prelevementsMensuels.value = data.items || []
    prelevementsStats.value = data.stats || { total_attendu: 0, total_effectue: 0, percent: 0 }
  } catch (error: any) {
    console.error('Erreur chargement prélèvements mensuels:', error)
  } finally {
    prelevementsLoading.value = false
  }
}

async function loadDecaissementsMensuels(id: number) {
  decaissementsLoading.value = true
  try {
    const data = await membersService.getMemberDecaissementsMensuels(id)
    decaissementsMensuels.value = data.items || []
    decaissementsStats.value = data.stats || {
      debloque: 0, debloque_percent: 0,
      bloque: 0, bloque_percent: 0,
      total: 0,
    }
  } catch (error: any) {
    console.error('Erreur chargement décaissements mensuels:', error)
  } finally {
    decaissementsLoading.value = false
  }
}

async function loadWalletInfos(id: number) {
  walletLoading.value = true
  try {
    const data = await membersService.getMemberWalletInfos(id)
    walletInfos.value = data || walletInfos.value
  } catch (error: any) {
    console.error('Erreur chargement wallet infos:', error)
  } finally {
    walletLoading.value = false
  }
}

async function loadCartes(id: number) {
  try {
    const data = await membersService.getMemberCartes(id)
    cartes.value = data.cartes || []
  } catch (error: any) {
    console.error('Erreur chargement cartes:', error)
  }
}

async function validatePrelevementMensuel(detail: PrelevementMensuel) {
  if (!member.value) return
  if (validatingPrelevementId.value !== null) return

  const confirmation = await showConfirm(
    `Valider le prélèvement de ${formatAmount(detail.montant)} F pour ${formatPrelevementDateLong(detail.date_prevision)} ?`,
    'Confirmation de validation'
  )
  if (!confirmation) return

  validatingPrelevementId.value = detail.id
  try {
    await membersService.validateMemberPrelevementMensuel(member.value.id, detail.id)
    await loadPrelevementsMensuels(member.value.id)
    await showAlert.success('Prélèvement validé avec succès.', 'Validation réussie')
  } catch (error: any) {
    await showAlert.error(error.message || 'Erreur lors de la validation.', 'Erreur')
  } finally {
    validatingPrelevementId.value = null
  }
}

function formatPrelevementDate(dateString: string | null): string {
  if (!dateString) return '-'
  const monthsFr = ['Jan', 'Fév', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc']
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return '-'
  return `${String(date.getDate()).padStart(2, '0')} ${monthsFr[date.getMonth()]} ${date.getFullYear()}`
}

function formatPrelevementDateLong(dateString: string | null): string {
  if (!dateString) return '-'
  const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return '-'
  return `${String(date.getDate()).padStart(2, '0')} ${monthsFr[date.getMonth()]} ${date.getFullYear()}`
}

async function loadMember(id: number) {
  try {
    loading.value = true
    errorMessage.value = ''
    const data = await membersService.getMember(id)
    member.value = data
  } catch (error: any) {
    errorMessage.value = error.message || 'Erreur lors du chargement du membre'
    // Rediriger vers la liste si le membre n'est pas trouvé
    setTimeout(() => {
      router.push({ name: 'collectivites-membres' })
    }, 2000)
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push({ name: 'collectivites-membres' })
}

function goToCarte() {
  if (member.value) {
    router.push({ name: 'collectivites-membre-carte', params: { id: member.value.id } })
  }
}
</script>

<style scoped>
.souscription-block {
    border-radius: 20px;
}

.souscription-title {
    margin: -3rem 0 1.5rem -1.5rem;
}

.souscription-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1.5fr);
    gap: 1.5rem;
    align-items: start;
}

.souscription-grid > * {
    min-width: 0;
}

@media (max-width: 1100px) {
    .souscription-grid {
        grid-template-columns: 1fr;
    }
}

.souscription-identity {
    display: flex;
    gap: 1.25rem;
    align-items: flex-start;
    min-width: 0;
}

.souscription-avatar {
    flex: 0 0 90px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid rgba(var(--primary-color-rgb), 0.5);
}

.souscription-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.souscription-identity-body {
    flex: 1 1 auto;
    min-width: 0;
    color: rgb(var(--color-white-rgb), 0.95);
}

.souscription-identity-body p {
    margin: 0;
    word-break: break-word;
}

.souscription-email {
    word-break: break-all;
    overflow-wrap: anywhere;
    color: rgb(var(--color-white-rgb), 0.85);
}

.souscription-dates {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    color: rgb(var(--color-white-rgb), 0.95);
}

.souscription-dates p {
    margin: 0;
}

.souscription-hr {
    border: 0;
    border-top: 1px solid rgb(var(--color-white-rgb), 0.15);
    margin: 0.5rem 0;
}

.souscription-widgets-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
}

@media (max-width: 600px) {
    .souscription-widgets-grid {
        grid-template-columns: 1fr;
    }
}

.souscription-card {
    background-color: rgba(0, 0, 0, 0.45);
    border-radius: 20px;
    padding: 1.25rem 1.5rem;
    color: rgb(var(--color-white-rgb), 0.95);
    min-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.souscription-card-label {
    margin: 0;
    font-size: 0.85rem;
    color: rgb(var(--color-white-rgb), 0.7);
    margin-bottom: 0.35rem;
}

.souscription-card-value {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 700;
    color: rgb(var(--primary-color-rgb), 1);
    line-height: 1.1;
}

.souscription-card-extra {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    color: rgb(var(--color-white-rgb), 0.65);
}

.souscription-progress-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.souscription-progress-bar {
    height: 40px;
    border-radius: 100px;
    background-color: rgba(var(--color-white-rgb), 0.12);
    overflow: hidden;
}

.souscription-progress-fill {
    height: 100%;
    border-radius: 100px;
    transition: width 0.3s ease;
}
</style>

